import React from "react";
import { useTranslation } from "react-i18next";
import Heading from "@/components/atoms/Heading";
import { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "./index";
import { Grid } from "@mantine/core";
import Divider from "@/components/atoms/Divider";
import Text from "@/components/atoms/Text";
import { useAuth } from "@/contexts/AuthContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { UpdateUserFormInput, UpdateUserValidationSchema } from "./validation/UserSettingsValidationSchemas";
import TextInput from "@/components/atoms/TextInput";
import Checkbox from "@/components/atoms/Checkbox";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import PhoneField from "@/components/atoms/PhoneInput/PhoneInput";
import CTA from "@/components/atoms/CTA";
import { useGetLanguages } from "@/services/RegisterCompany";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateUser } from "@/services/Settings";
import { changeLocale } from "@/utils/sessionStorage";

import { notifications } from "@mantine/notifications";
import Stack from "@/components/atoms/Stack";
import Spinner from "@/components/atoms/Spinner/Spinner";
import { User } from "@/types";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

export default function UserSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  return (
    <Stack data-testid={"user-settings"}>
      <Heading data-testid={"page-header"} order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.myProfile")}
      </Heading>
      {user && <UserSettingsForm user={user} />}
    </Stack>
  );
}

interface UserSettingsFormProps {
  user: User;
}

const UserSettingsForm: React.FC<UserSettingsFormProps> = ({ user }) => {
  const { t } = useTranslation();

  const { data: languageOptions, isLoading: isLoadingLanguageOptions } = useGetLanguages();
  const { i18n } = useTranslation();
  const { currentCompany } = useAuth();
  const queryClient = useQueryClient();

  if (!user) {
    return <Spinner loading={true} />;
  }

  const { mutate: updateUserMutation, isPending } = useMutation({
    mutationFn: (data: UpdateUserFormInput) => {
      return updateUser({ ...data, languageId: parseInt(data.languageId) });
    },
    onSuccess: (_, variables) => {
      const newLanguage = languageOptions?.find((lng) => lng.id === Number(variables.languageId));
      if (newLanguage && newLanguage.languageCode !== i18n.language) {
        changeLocale(newLanguage.languageCode, i18n, currentCompany?.companyTimeZone);
      }
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:settings.userUpdated"),
          "user-updated-success"
        )
      );
      queryClient.invalidateQueries();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:settings.userUpdatedError"),
          "user-updated-error"
        )
      );
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<UpdateUserFormInput>({
    resolver: zodResolver(UpdateUserValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    values: {
      userId: user.id,
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      phone: user.phone || "",
      languageId: user.language?.id.toString(),
      receiveCopyOfTradingNotifications: user.receiveCopyOfTradingNotifications || false,
    },
  });

  const onSubmit = (data: UpdateUserFormInput) => {
    updateUserMutation(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text data-testid="user-details-header" size="md" strong={true} display="technical" tt="uppercase">
              {t("common:details")}
            </Text>
          </SettingsHeader>
          <Grid.Col span={12}>
            <Text data-testid="user-details-user" size="sm" strong={true} display="technical" tt="uppercase">
              {t("common:user")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="firstname"
              render={({ field, fieldState }) => {
                return (
                  <TextInput
                    {...field}
                    id="firstname"
                    required={true}
                    label={t("common:firstname")}
                    data-testid="firstname-input"
                    value={field.value || ""}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="lastname"
              render={({ field, fieldState }) => {
                return (
                  <TextInput
                    {...field}
                    id="lastname"
                    required={true}
                    label={t("common:lastname")}
                    data-testid="lastname-input"
                    value={field.value || ""}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState }) => {
                return (
                  <TextInput
                    {...field}
                    id="email"
                    data-testid="email-input"
                    required={true}
                    label={t("common:email")}
                    value={field.value || ""}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="phone"
              render={({ field, fieldState }) => {
                return (
                  <PhoneField
                    {...field}
                    placeholder={t("common:phone")}
                    data-testid="phone-input"
                    errormessage={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Controller
              control={control}
              name="receiveCopyOfTradingNotifications"
              render={({ field, fieldState }) => {
                return (
                  <Checkbox
                    {...field}
                    id="receiveCopyOfTradingNotifications"
                    label={t("settings:receiveCopyOfTradingNotifications")}
                    data-testid="receiveCopyOfTradingNotifications-checkbox"
                    checked={field.value}
                    value={field.value.toString()}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
          <Grid.Col span={12}>
            <Text data-testid={"user-details-language"} size="sm" strong={true} display="technical" tt="uppercase">
              {t("common:language")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Controller
              control={control}
              name="languageId"
              render={({ field, fieldState }) => {
                return (
                  <Select
                    {...field}
                    id="languageId"
                    label={t("actions:selectLanguage")}
                    data-testid="language-select"
                    defaultValue={field.value?.toString() || "1"}
                    data={transformDataToSelectOption(
                      languageOptions || [],
                      "id",
                      "languageCode",
                      t,
                      "enumerables:languages"
                    )}
                    value={field.value?.toString() || "1"}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    loading={isLoadingLanguageOptions}
                  />
                );
              }}
            />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA disabled={!isValid} id="save" size="lg" type="submit" loading={isPending} data-testid={"submit-btn"}>
          {t("actions:save")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
};
